import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Eye, EyeOff, Sparkles, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import logo from '../assets/NTZLOGOnew.png';

const InviteAccept = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [status, setStatus] = useState(null); // 'accepted' | 'declined' | null
  const [visible, setVisible] = useState(false);

  const { acceptInvite, clearSessionForInvite } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  // Clear any stale auth session on mount so fetchUser / interceptors don't interfere
  useEffect(() => {
    clearSessionForInvite();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!token || !email) {
      toast.error('Invalid invite link');
      navigate('/login');
    }
  }, [token, email, navigate]);

  const inputStyle = {
    border: isDark ? '1.5px solid rgba(100,160,255,0.18)' : '1.5px solid rgba(0,0,0,0.1)',
    background: isDark ? '#0f1729' : '#fafafa',
    color: isDark ? '#f0f6fc' : '#0f172a',
  };
  const handleFocus = e => {
    e.target.style.border = '1.5px solid #F87217';
    e.target.style.boxShadow = '0 0 0 3px rgba(248,114,23,0.1)';
    e.target.style.background = isDark ? '#162040' : '#fff';
  };
  const handleBlur = e => {
    e.target.style.border = isDark ? '1.5px solid rgba(100,160,255,0.18)' : '1.5px solid rgba(0,0,0,0.1)';
    e.target.style.boxShadow = 'none';
    e.target.style.background = isDark ? '#0f1729' : '#fafafa';
  };

  const handleAccept = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Please enter your name'); return; }
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await acceptInvite(token, email, password, name);
      setStatus('accepted');
      toast.success('Welcome! Your account has been created.');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to accept invite';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    setDeclining(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      await fetch(`${API}/invite/decline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email }),
      });
      setStatus('declined');
      toast.info('Invite declined.');
    } catch {
      toast.error('Failed to decline invite');
    } finally {
      setDeclining(false);
    }
  };

  const bg = isDark ? '#0a0f1e' : '#fff';
  const textPrimary = isDark ? '#f0f6fc' : '#0f172a';
  const textMuted = isDark ? '#8b949e' : '#64748b';

  if (status === 'accepted') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: bg }}>
        <div className="text-center space-y-4">
          <CheckCircle className="w-16 h-16 mx-auto text-green-500" />
          <h2 className="text-2xl font-bold" style={{ color: textPrimary }}>Account Created!</h2>
          <p style={{ color: textMuted }}>Redirecting you to dashboard...</p>
        </div>
      </div>
    );
  }

  if (status === 'declined') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: bg }}>
        <div className="text-center space-y-4">
          <XCircle className="w-16 h-16 mx-auto text-red-400" />
          <h2 className="text-2xl font-bold" style={{ color: textPrimary }}>Invite Declined</h2>
          <p style={{ color: textMuted }}>You have declined the invitation.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: bg }}>
      {/* Left: Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-8 md:px-14 lg:px-20 py-10">
        <div
          className="max-w-sm w-full mx-auto"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.5s ease, transform 0.5s ease',
          }}
        >
          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg,#F87217,#FF9A4E)' }}>
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold" style={{ color: textPrimary }}>Multi-Agentic</span>
          </div>

          <h1 className="text-3xl font-bold mb-1" style={{ color: textPrimary, fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.02em' }}>
            You're invited!
          </h1>
          <p className="mb-2 text-sm" style={{ color: textMuted }}>
            Set up your account to get started
          </p>
          <div
            className="mb-6 px-3 py-2 rounded-lg text-xs font-medium"
            style={{ background: 'rgba(248,114,23,0.1)', color: '#F87217', border: '1px solid rgba(248,114,23,0.2)' }}
          >
            Joining as: <strong>{email}</strong>
          </div>

          <form onSubmit={handleAccept} className="space-y-4">
            {/* Name */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold" style={{ color: isDark ? '#c9d1d9' : '#334155' }}>
                Full Name
              </label>
              <input
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full h-12 px-4 rounded-xl text-sm outline-none transition-all duration-200"
                style={inputStyle}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold" style={{ color: isDark ? '#c9d1d9' : '#334155' }}>
                Create Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min. 8 characters"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full h-12 px-4 pr-12 rounded-xl text-sm outline-none transition-all duration-200"
                  style={inputStyle}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {password.length > 0 && (
                <div className="flex gap-1 mt-1.5">
                  {[1, 2, 3, 4].map(n => (
                    <div key={n} className="h-1 flex-1 rounded-full transition-all duration-300"
                      style={{
                        background: password.length >= n * 3
                          ? n <= 1 ? '#ef4444' : n <= 2 ? '#f59e0b' : n <= 3 ? '#3b82f6' : '#10b981'
                          : isDark ? '#1e2d52' : '#e2e8f0',
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Accept */}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl text-white font-bold text-sm transition-all duration-200"
              style={{
                background: loading ? '#f0a067' : 'linear-gradient(135deg,#F87217,#FF9A4E)',
                boxShadow: loading ? 'none' : '0 6px 20px rgba(248,114,23,0.35)',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Setting up account...
                </span>
              ) : 'Accept & Create Account'}
            </button>

            {/* Decline */}
            <button
              type="button"
              onClick={handleDecline}
              disabled={declining}
              className="w-full h-10 rounded-xl text-sm font-medium transition-all duration-200"
              style={{
                background: 'transparent',
                border: isDark ? '1px solid rgba(100,160,255,0.18)' : '1px solid rgba(0,0,0,0.1)',
                color: textMuted,
                cursor: declining ? 'not-allowed' : 'pointer',
              }}
            >
              {declining ? 'Declining...' : 'Decline Invitation'}
            </button>
          </form>
        </div>
      </div>

      {/* Right: Visual Panel */}
      <div
        className="hidden lg:flex w-1/2 relative overflow-hidden items-center justify-center"
        style={{ background: 'linear-gradient(150deg,#0d1a3a 0%,#1a0a00 50%,#0d1a3a 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 40% 50%,rgba(248,114,23,0.2) 0%,transparent 55%)' }} />
        <div className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.025) 1px,transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        <div className="relative z-10 text-center px-8">
          <div className="mx-auto mb-8 flex justify-center" style={{ animation: 'float 5s ease-in-out infinite' }}>
            <img src={logo} alt="Logo" className="w-50 h-50 object-contain" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Welcome aboard</h2>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
            You've been granted access to the platform.<br />Set up your account to get started.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InviteAccept;